/**
 * Cycle-aware resolver for local JSON pointers (`$ref: '#/...'`).
 *
 * A generic dereferencer cannot be used here: for a recursive schema it replaces the back-edge
 * with a live pointer to an ancestor, producing a circular object graph. `@asyncapi/parser`
 * calls `JSON.stringify` while normalizing its input, so any such payload fails to render with
 * `TypeError: Converting circular structure to JSON`.
 *
 * Instead we mirror the approach of the original Elm viewer (`Json/ResolveLocalRefs.elm`): carry
 * the chain of pointers currently being expanded and stop when a `$ref` reappears in it. Where
 * the Elm version leaves the back-edge as an unexpanded `$ref`, we substitute a small placeholder
 * schema, because the payload is later rendered inside a synthetic specification that carries no
 * `components` section for a surviving `$ref` to resolve against.
 */

const unescapePointerToken = (token: string) =>
  token.replace(/~1/g, '/').replace(/~0/g, '~');

const isRecord = (value: unknown): value is Record<string, any> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** Walks a local JSON pointer such as `#/components/schemas/Thing`. */
const findPointer = (root: unknown, pointer: string): unknown => {
  if (pointer === '#' || pointer === '#/') {
    return root;
  }
  const tokens = pointer
    .replace(/^#\//, '')
    .split('/')
    .map(unescapePointerToken);

  let current: any = root;
  for (const token of tokens) {
    if (current === null || typeof current !== 'object') {
      return undefined;
    }
    current = Array.isArray(current) ? current[Number(token)] : current[token];
    if (current === undefined) {
      return undefined;
    }
  }
  return current;
};

/**
 * Stands in for a reference back to a schema that is already being expanded further up the tree.
 * Keeps `type`/`title` so the viewer still renders something meaningful for the branch.
 */
const buildRecursionPlaceholder = (pointer: string, target: unknown) => {
  const label = pointer.split('/').pop() ?? pointer;
  const placeholder: Record<string, unknown> = {
    description: `Recursive reference to \`${label}\` (not expanded).`,
  };
  if (isRecord(target)) {
    if (target.type) placeholder.type = target.type;
    if (target.title) placeholder.title = target.title;
  }
  if (!placeholder.type) placeholder.type = 'object';
  return placeholder;
};

/**
 * Returns a copy of `input` with every local `$ref` expanded in place. External and unresolvable
 * refs are left as they are, and recursive refs become a placeholder instead of a cycle, so the
 * result is always a finite tree that can be safely serialized.
 */
export const resolveLocalRefs = (input: any): any => {
  const walk = (node: unknown, chain: string[]): any => {
    if (node === null || typeof node !== 'object') {
      return node;
    }
    if (Array.isArray(node)) {
      return node.map(value => walk(value, chain));
    }

    const ref = (node as Record<string, unknown>).$ref;
    if (typeof ref === 'string') {
      if (!ref.startsWith('#')) {
        return node; // External reference: nothing we can resolve offline.
      }
      const target = findPointer(input, ref);
      if (target === undefined) {
        return node; // Dangling reference: keep it rather than losing the information.
      }
      if (chain.includes(ref)) {
        return buildRecursionPlaceholder(ref, target);
      }

      const expanded = walk(target, [...chain, ref]);
      // Keys sitting alongside a `$ref` (a description, an example, ...) override the target's.
      const { $ref: _ignored, ...siblings } = node as Record<string, unknown>;
      if (Object.keys(siblings).length === 0 || !isRecord(expanded)) {
        return expanded;
      }
      return { ...expanded, ...walk(siblings, chain) };
    }

    const resolved: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(node)) {
      resolved[key] = walk(value, chain);
    }
    return resolved;
  };

  return walk(input, []);
};
